import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SystemChipItem } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { useAuth } from "../../../contexts/AuthProvider";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useJobs } from "../../../hooks/useJobs";
import { useRosterLookup } from "../../../hooks/useRosterLookup";
import type { Nurse } from "../../../utils/models";
import { CHANNEL_LABEL_KEY, CHANNEL_VALUES } from "./activityLogGroupedDisplay";
import { type ActivityLogPerformedByOption, toMissingPerformedByOption, useActivityLogPerformedByOptionsQuery } from "./useActivityLogPerformedByOptionsQuery";

export type GroupedActivityLogFilterOptions = {
	nurseOptions: SystemAutoCompleteOption<Nurse>[];
	/** The chosen department's nurse ids once its own roster request has succeeded; `null` before
	 * that, when a nurse id can't yet be judged unknown. */
	departmentNurseIds: ReadonlySet<number> | null;
	/** The chosen department's roster request failed — a nurse id will never be judged, so nothing
	 * may wait on it. */
	departmentNursesFailed: boolean;
	channelItems: SystemChipItem<string>[];
	shiftJobOptions: DropdownItem<string>[];
	enabledJobIds: ReadonlySet<string>;
	/** True once the department's job configuration is in — before that a job id can't be judged retired. */
	jobsLoaded: boolean;
	performedByOptions: ActivityLogPerformedByOption[];
	performedByOptionsLoading: boolean;
	/** The option values the backend returned — already masked for this viewer, so authoritative — or
	 * `null` until they have loaded. */
	fetchedPerformedByValues: ReadonlySet<string> | null;
};

/**
 * Options and the unknown-nurse check both read THIS department's roster entry, so a nurse id is judged only once
 * that roster has loaded — and a department with no nurses still counts as loaded.
 */
const useNurseOptions = (): Pick<GroupedActivityLogFilterOptions, "nurseOptions" | "departmentNurseIds" | "departmentNursesFailed"> => {
	const { chosenDepartment } = useChosenDepartment();
	const { roster, getNurseFormattedName, isError: departmentNursesFailed } = useRosterLookup(chosenDepartment.department_id);
	const nurseOptions = useMemo(
		// DEFAULT: no options until the roster loads; `departmentNurseIds` stays null meanwhile, so nothing is judged unknown.
		() => (roster ?? []).map((nurse) => ({ label: getNurseFormattedName(nurse.id), value: nurse })).sort((a, b) => a.label.localeCompare(b.label)),
		[roster, getNurseFormattedName],
	);
	const departmentNurseIds = useMemo(() => (roster ? new Set(roster.map((nurse) => nurse.id)) : null), [roster]);
	return { nurseOptions, departmentNurseIds, departmentNursesFailed };
};

/** Same department-enabled jobs list MasterSchedule v2's job filter uses. */
const useShiftJobOptions = (): Pick<GroupedActivityLogFilterOptions, "shiftJobOptions" | "enabledJobIds" | "jobsLoaded"> => {
	const { chosenDepartment } = useChosenDepartment();
	const shiftJobs = useJobs();
	const jobsLoaded = chosenDepartment.jobIds !== undefined && chosenDepartment.jobs !== undefined;
	return useMemo(
		() => ({
			shiftJobOptions: shiftJobs.map((job) => ({ value: job.id, label: job.displayName })),
			enabledJobIds: new Set(shiftJobs.map((job) => job.id)),
			jobsLoaded,
		}),
		[shiftJobs, jobsLoaded],
	);
};

/**
 * A selected Performed By value the fetched options don't carry (a shared link, a user with no
 * activity here any more) is still shown so it can be seen and removed — same approach as the Team
 * page's `buildSourceListOptions` — masked for a non-support viewer (see `toMissingPerformedByOption`).
 */
const usePerformedByOptions = (
	selectedPerformedBy: string[],
): Pick<GroupedActivityLogFilterOptions, "performedByOptions" | "performedByOptionsLoading" | "fetchedPerformedByValues"> => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	// DEFAULT: no user info yet reads as non-support — the side that masks, so nothing is revealed.
	const isSupport = !!userInfo?.support;
	const { data, isLoading } = useActivityLogPerformedByOptionsQuery();
	const fetchedPerformedByValues = useMemo(() => (data ? new Set(data.map((option) => option.value)) : null), [data]);
	const performedByOptions = useMemo(() => {
		// DEFAULT: no options yet (loading or failed) — the selected values below still render.
		const fetched = data ?? [];
		const missing = selectedPerformedBy
			.filter((value) => !fetchedPerformedByValues?.has(value))
			.map((value) => toMissingPerformedByOption(value, isSupport, t));
		return missing.length > 0 ? [...fetched, ...missing] : fetched;
	}, [data, fetchedPerformedByValues, selectedPerformedBy, isSupport, t]);
	return { performedByOptions, performedByOptionsLoading: isLoading, fetchedPerformedByValues };
};

/** Every option list the Grouped Activity Log's filters resolve against. */
export function useGroupedActivityLogFilterOptions({ selectedPerformedBy }: { selectedPerformedBy: string[] }): GroupedActivityLogFilterOptions {
	const { t } = useTranslation();
	const channelItems = useMemo<SystemChipItem<string>[]>(() => CHANNEL_VALUES.map((value) => ({ value, label: t(CHANNEL_LABEL_KEY[value]) })), [t]);
	return { ...useNurseOptions(), ...useShiftJobOptions(), ...usePerformedByOptions(selectedPerformedBy), channelItems };
}
